// Shared prospect-page ordering for New Business proposals.
// layout_order = ["product:<id>", "bundle:<id>", ...]. Empty = legacy automatic order.
export const PREFERRED_CATEGORY_ORDER = ["Photography", "Floorplans", "Video", "3D Showcase", "Social"];
export const ALWAYS_LAST_CATEGORIES = ["EPCs"];

// Default small print under the pricing on the prospect page (editable per proposal; "" hides it)
export const DEFAULT_SMALL_PRINT = "Annexes & extra dwellings: EPC £80. Additional floorplan £65.";

export const productKey = (productId) => `product:${productId}`;
export const bundleKey = (bundle, idx) => `bundle:${bundle?.id || `#${idx}`}`;

// Fixed page blocks that can be positioned among the service sections (hero + footer stay put).
export const FIXED_BLOCKS = [
  { key: "block:pricing", id: "pricing", label: "Individual services", desc: "Per-property pricing table" },
  { key: "block:impact", id: "impact", label: "See the Impact", desc: "ROI calculator" },
  { key: "block:notes", id: "notes", label: "Notes", desc: "From your account manager" },
  { key: "block:getstarted", id: "getstarted", label: "Get started", desc: "Accept · Book · Account" },
];
const FIXED_BY_KEY = Object.fromEntries(FIXED_BLOCKS.map((b) => [b.key, b]));

const categoryRank = (cat) => {
  const i = PREFERRED_CATEGORY_ORDER.indexOf(cat);
  if (i !== -1) return i;
  if (ALWAYS_LAST_CATEGORIES.includes(cat)) return 1000 + ALWAYS_LAST_CATEGORIES.indexOf(cat);
  return 500;
};

// Stable sort of selected products (with .product resolved) into the recommended category order.
export function recommendedProductOrder(selectedFull) {
  return selectedFull
    .map((sp, i) => ({ sp, i }))
    .sort((a, b) => categoryRank(a.sp.product?.category) - categoryRank(b.sp.product?.category) || a.i - b.i)
    .map((x) => x.sp);
}

// Recommended full key list: products in category order, the packages block, then the fixed blocks.
export function recommendedLayoutOrder(selectedFull, bundles) {
  return [
    ...recommendedProductOrder(selectedFull).map((sp) => productKey(sp.product_id)),
    ...(bundles || []).map((b, i) => bundleKey(b, i)),
    ...FIXED_BLOCKS.map((b) => b.key),
  ];
}

/**
 * Turn the saved order into render blocks:
 *   { type: "section", category, products: [sp] }   — one per category, at first appearance
 *   { type: "packages", bundles: [b] }               — where the first bundle appears
 *   { type: "fixed", id, key, label }                — pricing / impact / notes / getstarted
 * Anything not in layoutOrder is appended (products in recommended order, bundles, then fixed blocks).
 */
export function buildLayoutBlocks(selectedFull, bundles, layoutOrder) {
  const keys = Array.isArray(layoutOrder) ? layoutOrder : [];
  const productByKey = Object.fromEntries(selectedFull.map((sp) => [productKey(sp.product_id), sp]));
  const bundleByKey = Object.fromEntries((bundles || []).map((b, i) => [bundleKey(b, i), b]));

  const blocks = [];
  const sectionByCat = {};
  let packagesBlock = null;
  const seen = new Set();

  const addProduct = (sp) => {
    const cat = sp.product?.category || "Other";
    if (!sectionByCat[cat]) {
      sectionByCat[cat] = { type: "section", key: `section:${cat}`, category: cat, products: [] };
      blocks.push(sectionByCat[cat]);
    }
    sectionByCat[cat].products.push(sp);
  };
  const addBundle = (b) => {
    if (!packagesBlock) {
      packagesBlock = { type: "packages", key: "packages", bundles: [] };
      blocks.push(packagesBlock);
    }
    packagesBlock.bundles.push(b);
  };
  const addFixed = (def) => blocks.push({ type: "fixed", id: def.id, key: def.key, label: def.label });

  keys.forEach((k) => {
    if (seen.has(k)) return;
    if (productByKey[k]) { seen.add(k); addProduct(productByKey[k]); }
    else if (bundleByKey[k]) { seen.add(k); addBundle(bundleByKey[k]); }
    else if (FIXED_BY_KEY[k]) { seen.add(k); addFixed(FIXED_BY_KEY[k]); }
  });
  recommendedProductOrder(selectedFull).forEach((sp) => {
    if (!seen.has(productKey(sp.product_id))) addProduct(sp);
  });
  (bundles || []).forEach((b, i) => {
    if (!seen.has(bundleKey(b, i))) addBundle(b);
  });
  FIXED_BLOCKS.forEach((def) => {
    if (!seen.has(def.key)) addFixed(def);
  });

  const orderedProducts = blocks.filter((b) => b.type === "section").flatMap((b) => b.products);
  const orderedBundles = packagesBlock ? packagesBlock.bundles : [];
  return { blocks, orderedProducts, orderedBundles };
}

// Saved keys that still exist, followed by anything new (products in recommended order, bundles, fixed blocks).
export function resolveLayoutOrder(selectedFull, bundles, layoutOrder) {
  const valid = new Set([
    ...selectedFull.map((sp) => productKey(sp.product_id)),
    ...(bundles || []).map((b, i) => bundleKey(b, i)),
    ...FIXED_BLOCKS.map((b) => b.key),
  ]);
  const out = [];
  (layoutOrder || []).forEach((k) => { if (valid.has(k) && !out.includes(k)) out.push(k); });
  recommendedLayoutOrder(selectedFull, bundles).forEach((k) => { if (!out.includes(k)) out.push(k); });
  return out;
}
